/**
 * Roadmap Review — NEW WORK commissioned by the owner (2026-11-04), not a port.
 *
 * "Calculate Schedule" no longer saves straight away. It opens this review with the calculated
 * roadmap as a PROPOSAL. The client adjusts any phase date (or enters the roadmap by hand), the
 * feasibility check runs on every change (the scheduler's own rules: phase order, effort,
 * dependencies, group start, locked apps, shared platforms), an AI review gives a partner-level
 * verdict with the issues and what to change, and only CONFIRM saves the roadmap and records
 * who confirmed which version.
 *
 * Server: routes/roadmapReview.ts.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authFetch } from '../compat/api';
import { DispositionChip } from '../compat/DispositionChip';
import { Banner, Icon, icons } from '@platform/ui-kit/workspace';
import { WS_CARD, WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType } from '../workspace/ws';
import { PHASES, GANTT_PHASE_FILL, businessDays, fmtLong, fmtMDY, phaseLabel, type PhaseKey } from '../compat/roadmapPhases';
import { dayKey, holidaysBetween } from '../compat/ganttCalendar';

/* ── Business days, the scheduler's calendar (ganttCalendar mirrors businessDayCalendar.ts) ── */
const toDate = (ymd: string) => new Date(ymd + 'T12:00:00');
const toYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const holidayCache = new Map<number, Set<string>>();
function isBiz(d: Date): boolean {
  if (d.getDay() === 0 || d.getDay() === 6) return false;
  const y = d.getFullYear();
  if (!holidayCache.has(y)) holidayCache.set(y, new Set(holidaysBetween(new Date(y, 0, 1), new Date(y, 11, 31)).keys()));
  return !holidayCache.get(y)!.has(dayKey(d));
}
/** The scheduler's addBusinessDays: the start counts as day 1 when it is a business day. */
function addBiz(startYmd: string, days: number): string {
  const d = toDate(startYmd);
  let n = isBiz(d) ? 1 : 0;
  while (n < Math.max(1, days)) { d.setDate(d.getDate() + 1); if (isBiz(d)) n++; }
  return toYmd(d);
}
/** The first business day after a date (the next phase's start). */
function nextBiz(ymd: string, plusDays = 1): string {
  const d = toDate(ymd);
  d.setDate(d.getDate() + plusDays);
  while (!isBiz(d)) d.setDate(d.getDate() + 1);
  return toYmd(d);
}
function onOrAfterBiz(ymd: string): string {
  const d = toDate(ymd);
  while (!isBiz(d)) d.setDate(d.getDate() + 1);
  return toYmd(d);
}
/** The scheduler's default phase durations when an app has no effort plan. */
const DEFAULT_DAYS: Record<PhaseKey, number> = { assessment: 15, design: 20, migration: 25, testing: 15, golive: 10 };

type PhaseDates = Record<PhaseKey, { start: string | null; end: string | null }>;
interface PlanRow { integration_group_id: string; application_id: string; phases: PhaseDates }
interface Issue { id: string; severity: 'blocking' | 'warning' | 'info'; rule: string; integration_group_id: string; application_id: string | null; phase: string | null; message: string; recommendation: string }
interface GroupMetric {
  integration_group_id: string; group_name: string; earliest_start_date: string | null;
  baseline: { start: string | null; end: string | null; business_days: number | null };
  proposed: { start: string | null; end: string | null; business_days: number | null };
  golive_shift_days: number | null; changed_applications: string[]; critical_path: string[];
}
interface AiIssue { severity: 'blocking' | 'major' | 'minor'; category: string; integration_group_id: string; application_ids: string[]; phase: string; finding: string; impact: string; recommendation: string }
/** Owner request 2026-11-04: the server's facts for one cross-group dependency, with the AI's explanation. */
interface AiCrossGroup {
  source_group: string; source_application_id: string; target_group: string; target_application_id: string;
  rule: string; dependency_date: string | null; group_earliest_start: string | null;
  governs: 'dependency' | 'group_start' | 'no_source_schedule'; earliest_start: string | null;
  proposed_start: string | null; respected: boolean | null; explanation: string;
}
interface AiReview { verdict: 'approved' | 'approved_with_conditions' | 'not_feasible'; headline: string; executive_summary: string[]; issues: AiIssue[]; critical_path_commentary: string; cross_group_explanations?: AiCrossGroup[]; recommendations: string[]; confidence: string }
const GOVERNS: Record<AiCrossGroup['governs'], string> = {
  dependency: 'Dependency governs',
  group_start: 'Group start date governs',
  no_source_schedule: 'Source not scheduled',
};
interface ReviewResult {
  feasible: boolean; counts: { blocking: number; warning: number; info: number }; issues: Issue[]; metrics: GroupMetric[];
  baseline: PlanRow[]; plan: PlanRow[];
  applications: { integration_group_id: string; application_id: string; application_name: string; recommendation: string | null }[];
  // Owner ruling 2026-11-04: the source sits in integration_group_id, the target in target_integration_group_id;
  // source_phases carries a cross-group source's saved dates when its group is not under review.
  dependencies: { integration_group_id: string; target_integration_group_id?: string; source_phases?: PlanRow['phases'] | null; source_application_id: string; target_application_id: string; dependency_type: string; source_phase_name: string; target_phase_name?: string; lag_days?: number | null; notes: string | null }[];
  effort?: { integration_group_id: string; application_id: string; phase: PhaseKey; days: number }[];
  statuses?: { integration_group_id: string; application_id: string; deployment_status: string }[];
}
interface Confirmation { integration_group_id: string; verdict: string; headline: string | null; golive_date: string | null; confirmed_by: string; confirmed_at: string }
const LOCKED = new Set(['Approved', 'In Development', 'Deployed']);
const VERDICT_TEXT: Record<string, string> = { approved: 'Approved', approved_with_conditions: 'Approved with conditions', not_feasible: 'Not feasible', rules_passed: 'Rule check passed' };

const k = (g: string, a: string) => `${g}|${a}`;
const samePlan = (a?: PlanRow, b?: PlanRow) => !!a && !!b && PHASES.every((p) => a.phases[p.key].start === b.phases[p.key].start && a.phases[p.key].end === b.phases[p.key].end);
const shiftLabel = (n: number | null) => (n == null ? '—' : n === 0 ? 'on plan' : `${n > 0 ? '+' : '−'}${Math.abs(n)} days`);

const VERDICT = {
  approved: { label: 'Approved', tone: 'success' as const, icon: icons.CheckCircle2 },
  approved_with_conditions: { label: 'Approved with conditions', tone: 'warning' as const, icon: icons.AlertTriangle },
  not_feasible: { label: 'Not feasible', tone: 'error' as const, icon: icons.XCircle },
};
const SEV = {
  blocking: { label: 'Blocking', tone: 'error' as const },
  major: { label: 'Major', tone: 'warning' as const },
  warning: { label: 'Warning', tone: 'warning' as const },
  minor: { label: 'Minor', tone: 'info' as const },
  info: { label: 'Info', tone: 'neutral' as const },
};

export interface RoadmapReviewProps {
  groupIds: string[];
  /** 'proposal' = run the scheduler without saving (Calculate Schedule); 'current' = review what is saved. */
  mode: 'proposal' | 'current';
  onClose: () => void;
  onConfirmed: () => void;
}

export default function RoadmapReview({ groupIds, mode, onClose, onConfirmed }: RoadmapReviewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReviewResult | null>(null);
  const [proposal, setProposal] = useState<PlanRow[]>([]); // what Calculate produced (or what is saved)
  const [plan, setPlan] = useState<PlanRow[]>([]);         // the client's working copy
  const [checking, setChecking] = useState(false);
  const [ai, setAi] = useState<AiReview | null>(null);
  const [aiState, setAiState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [aiError, setAiError] = useState('');
  const [aiStale, setAiStale] = useState(false);
  // Owner request 2026-11-04: an expanded app stays expanded until ITS OWN arrow is clicked
  // again — several can be open at once. `focus` is the app clicked last (it filters the
  // Dependencies panel).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState<string | null>(null);
  const toggle = (key: string) => {
    setExpanded((prev) => { const n = new Set(prev); if (n.has(key)) { n.delete(key); } else { n.add(key); } return n; });
    setFocus((prev) => (expanded.has(key) ? (prev === key ? null : prev) : key));
  };
  const reveal = (key: string) => { setExpanded((prev) => new Set(prev).add(key)); setFocus(key); };
  const [focusApp, setFocusApp] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Owner request 2026-11-04: dates fill themselves from the effort plan — set a phase's start
  // and its end follows, and the app's later phases move with it.
  const [autoFill, setAutoFill] = useState(true);
  const [confirmed, setConfirmed] = useState<{ by: string; at: string } | null>(null);
  // Confirmations already on record for these groups (owner request 2026-11-04: a confirmed
  // group opens in the same Roadmap view, in its confirmed state).
  const [onRecord, setOnRecord] = useState<Confirmation[]>([]);
  const timer = useRef<number | undefined>(undefined);
  const proposalRef = useRef<PlanRow[]>([]);
  useEffect(() => { proposalRef.current = proposal; }, [proposal]);

  const groupsKey = groupIds.join(',');

  /* ── load: the proposal (Calculate) or the saved schedule ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await authFetch(`/api/roadmap-review/${mode === 'proposal' ? 'propose' : 'validate'}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ group_ids: groupIds }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || 'Could not prepare the roadmap');
        if (cancelled) return;
        const st = await authFetch(`/api/roadmap-review/status?groups=${encodeURIComponent(groupIds.join(','))}`).then((x) => (x.ok ? x.json() : [])).catch(() => []);
        if (cancelled) return;
        setOnRecord(Array.isArray(st) ? st : []);
        setResult(j);
        setProposal(j.plan);
        setPlan(j.plan);
        proposalRef.current = j.plan;
        recheck(j.plan);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsKey, mode]);

  /* ── re-check on every change (debounced) ── */
  const recheck = useCallback((next: PlanRow[]) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      setChecking(true);
      try {
        const r = await authFetch('/api/roadmap-review/validate', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ group_ids: groupIds, proposed: next, reference: proposalRef.current }),
        });
        const j = await r.json();
        if (r.ok) setResult((prev) => (prev ? { ...j, baseline: prev.baseline } : j));
      } finally {
        setChecking(false);
      }
    }, 350);
  }, [groupIds]);

  const effortOf = (g: string, a: string, p: PhaseKey) =>
    result?.effort?.find((e) => e.integration_group_id === g && e.application_id === a && e.phase === p)?.days || DEFAULT_DAYS[p];
  /** Set one date; with auto-fill on, this phase's end and every later phase follow the effort plan. */
  const reflow = (r: PlanRow, phase: PhaseKey, field: 'start' | 'end', value: string): PlanRow => {
    const phases = { ...r.phases, [phase]: { ...r.phases[phase], [field]: value || null } } as PhaseDates;
    if (!autoFill || !value) return { ...r, phases };
    const i = PHASES.findIndex((p) => p.key === phase);
    if (field === 'start') phases[phase] = { start: value, end: addBiz(value, effortOf(r.integration_group_id, r.application_id, phase)) };
    let prevEnd = phases[phase].end;
    for (const p of PHASES.slice(i + 1)) {
      if (!prevEnd) break;
      const s = nextBiz(prevEnd);
      phases[p.key] = { start: s, end: addBiz(s, effortOf(r.integration_group_id, r.application_id, p.key)) };
      prevEnd = phases[p.key].end;
    }
    return { ...r, phases };
  };
  const edit = (g: string, a: string, phase: PhaseKey, field: 'start' | 'end', value: string) => {
    setPlan((prev) => {
      const next = prev.map((r) => (r.integration_group_id === g && r.application_id === a ? reflow(r, phase, field, value) : r));
      recheck(next);
      return next;
    });
    if (ai) setAiStale(true);
    setConfirmed(null);
  };
  const resetRow = (g: string, a: string) => {
    const orig = proposal.find((r) => r.integration_group_id === g && r.application_id === a);
    if (!orig) return;
    setPlan((prev) => { const next = prev.map((r) => (r.integration_group_id === g && r.application_id === a ? orig : r)); recheck(next); return next; });
  };
  const resetAll = () => { setPlan(proposal); recheck(proposal); setAiStale(!!ai); };

  const runAi = async () => {
    setAiState('running'); setAiError('');
    try {
      const r = await authFetch('/api/roadmap-review/ai', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ group_ids: groupIds, proposed: plan, reference: proposal }),
      });
      const j = await r.json();
      if (!r.ok || !j.ai) throw new Error(j.error || 'The AI review did not return a result');
      setAi(j.ai); setAiState('done'); setAiStale(false);
      setResult((prev) => (prev ? { ...j, baseline: prev.baseline } : j));
    } catch (e: any) {
      setAiError(e.message); setAiState('error');
    }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await authFetch('/api/roadmap-review/confirm', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ group_ids: groupIds, proposed: plan, reference: proposal, ai }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Could not confirm');
      setConfirmed({ by: j.confirmed_by, at: j.confirmed_at });
      setProposal(plan);
      authFetch(`/api/roadmap-review/status?groups=${encodeURIComponent(groupIds.join(','))}`).then((x) => (x.ok ? x.json() : [])).then((st) => setOnRecord(Array.isArray(st) ? st : [])).catch(() => {});
      onConfirmed();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setConfirming(false);
    }
  };

  /* ── derived ── */
  const appMeta = useMemo(() => new Map((result?.applications || []).map((a) => [k(a.integration_group_id, a.application_id), a])), [result]);
  const propByKey = useMemo(() => new Map(proposal.map((r) => [k(r.integration_group_id, r.application_id), r])), [proposal]);
  const issuesByApp = useMemo(() => {
    const m = new Map<string, Issue[]>();
    for (const i of result?.issues || []) if (i.application_id) {
      const key = k(i.integration_group_id, i.application_id);
      m.set(key, [...(m.get(key) || []), i]);
    }
    return m;
  }, [result]);
  const edited = plan.filter((r) => !samePlan(r, propByKey.get(k(r.integration_group_id, r.application_id))));
  const span = useMemo(() => {
    const all = [...plan, ...proposal].flatMap((r) => PHASES.flatMap((p) => [r.phases[p.key].start, r.phases[p.key].end])).filter(Boolean) as string[];
    all.sort();
    const s = all[0] ? new Date(all[0] + 'T12:00:00').getTime() : 0;
    const e = all[all.length - 1] ? new Date(all[all.length - 1] + 'T12:00:00').getTime() : 1;
    return { s, e, months: monthTicks(all[0], all[all.length - 1]) };
  }, [plan, proposal]);
  const pos = (ymd: string | null) => (ymd ? ((new Date(ymd + 'T12:00:00').getTime() - span.s) / Math.max(1, span.e - span.s)) * 100 : 0);

  const statusOf = (g: string, a: string) => result?.statuses?.find((s) => s.integration_group_id === g && s.application_id === a)?.deployment_status || null;
  const allOnRecord = groupIds.length > 0 && groupIds.every((g) => onRecord.some((c) => c.integration_group_id === g));
  const blocking = result?.counts.blocking ?? 0;
  const isConfirmed = !!confirmed || (mode === 'current' && allOnRecord && edited.length === 0);
  const step = isConfirmed ? 4 : ai && !aiStale ? 3 : edited.length ? 2 : 1;
  const verdict = ai && !aiStale ? ai.verdict : result ? (result.feasible ? (result.counts.warning ? 'approved_with_conditions' : 'approved') : 'not_feasible') : null;

  if (loading) {
    return (
      <div style={{ ...WS_CARD, padding: 40, textAlign: 'center', color: WS_COLOR.textMuted }}>
        <div className="md-spinner" style={{ margin: '0 auto 12px' }} />
        {mode === 'proposal' ? 'Calculating the proposed roadmap…' : 'Loading the roadmap…'}
      </div>
    );
  }
  if (error && !result) {
    return <div style={{ padding: 16 }}><Banner tone="error">{error}</Banner></div>;
  }
  if (!result) return null;

  return (
    <div data-testid="roadmap-review" style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, flex: 1 }}>
      {/* ── Stepper + actions ── */}
      <div style={{ ...WS_CARD, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} data-testid="review-steps">
          {['Proposed roadmap', 'Client adjustments', 'Feasibility review', 'Confirmed'].map((label, i) => {
            const n = i + 1;
            const done = step > n || (n === 4 && isConfirmed);
            const current = step === n && !(n === 4 && isConfirmed);
            return (
              <React.Fragment key={label}>
                {i > 0 && <span style={{ width: 24, height: 2, background: done || current ? WS_COLOR.primary : WS_COLOR.border }} />}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...wsType('label'), color: done || current ? WS_COLOR.text : WS_COLOR.textMuted }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600,
                    background: done ? WS_STATUS.success.fill : current ? WS_COLOR.primary : WS_COLOR.surfaceSubtle,
                    color: done ? WS_STATUS.success.ink : current ? WS_COLOR.onPrimary : WS_COLOR.textMuted,
                  }}>{done ? <Icon icon={icons.Check} size={16} /> : n}</span>
                  {label}
                </span>
              </React.Fragment>
            );
          })}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {checking && <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>Checking…</span>}
          <button className="btn" onClick={onClose}><Icon icon={icons.ArrowLeft} size={20} /> Back to schedule</button>
          <button className="btn" onClick={resetAll} disabled={!edited.length}><Icon icon={icons.RefreshCw} size={20} /> Reset to proposal</button>
          <button className="btn btn-primary" onClick={confirm} disabled={confirming || blocking > 0 || isConfirmed} data-testid="review-confirm"
            title={blocking ? 'Resolve the blocking issues first' : 'Save this roadmap, approve its applications and record your confirmation'}>
            <Icon icon={icons.ClipboardCheck} size={20} /> {isConfirmed ? 'Confirmed' : confirming ? 'Confirming…' : 'Confirm roadmap'}
          </button>
        </div>
      </div>

      {error && <Banner tone="error" onDismiss={() => setError(null)}>{error}</Banner>}
      {!confirmed && onRecord.length > 0 && (
        <div data-testid="review-on-record">
          <Banner tone={isConfirmed ? 'success' : 'info'}>
            {onRecord.map((c) => `${c.integration_group_id} confirmed by ${c.confirmed_by} on ${new Date(c.confirmed_at).toLocaleString()} (${VERDICT_TEXT[c.verdict] || c.verdict}${c.golive_date ? `, go-live ${fmtLong(String(c.golive_date).slice(0, 10))}` : ''})`).join(' · ')}
            {!isConfirmed && edited.length > 0 && ' — the roadmap has changed since; confirm again to record the new version.'}
          </Banner>
        </div>
      )}
      {confirmed && (
        <Banner tone="success">Roadmap confirmed by {confirmed.by} on {new Date(confirmed.at).toLocaleString()}: saved to the schedule, and its applications are Approved.</Banner>
      )}

      {/* ── Programme scorecard: one tile per group ── */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(result.metrics.length, 3)}, minmax(0, 1fr))`, gap: 12, flexShrink: 0 }}>
        {result.metrics.map((m) => (
          <div key={m.integration_group_id} data-testid="review-kpi" style={{ ...WS_CARD, padding: '12px 16px', display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr', gap: 16, alignItems: 'center' }}>
            <div>
              <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text }}>{m.integration_group_id}</div>
              <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.group_name}</div>
            </div>
            <Kpi label="Go-live complete" value={fmtLong(m.proposed.end)} sub={shiftLabel(m.golive_shift_days)}
              tone={m.golive_shift_days == null || m.golive_shift_days === 0 ? 'neutral' : m.golive_shift_days > 0 ? 'warning' : 'success'} />
            <Kpi label="Programme length" value={m.proposed.business_days != null ? `${m.proposed.business_days} days` : '—'} sub="business days" tone="neutral" />
            <Kpi label="Adjusted apps" value={String(m.changed_applications.length)} sub={m.changed_applications.join(', ') || 'none'} tone={m.changed_applications.length ? 'info' : 'neutral'} />
          </div>
        ))}
      </div>

      {/* ── Plan (left) + Feasibility (right) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.75fr) minmax(340px, 1fr)', gap: 12, flex: 1, minHeight: 0 }}>
        {/* Plan */}
        <div style={{ ...WS_CARD, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${WS_COLOR.border}`, display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            <div style={{ ...wsType('sectionTitle'), color: WS_COLOR.text }}>Roadmap</div>
            <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>Click an application to adjust its phase dates. The dashed outline is the calculated proposal.</span>
            <label style={{ ...wsType('label'), color: WS_COLOR.text, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
              title="Set a phase's start date and its end — and every later phase — follow the effort plan">
              <input type="checkbox" checked={autoFill} onChange={(e) => setAutoFill(e.target.checked)} data-testid="review-autofill" />
              Fill end dates from effort
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
              {PHASES.map((p) => (
                <span key={p.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, ...wsType('meta'), color: WS_COLOR.textMuted, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: GANTT_PHASE_FILL[p.name] }} />{p.short}
                </span>
              ))}
            </div>
          </div>
          {/* month ruler */}
          <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', flexShrink: 0, borderBottom: `1px solid ${WS_COLOR.border}` }}>
            <div />
            <div style={{ position: 'relative', height: 26, marginRight: 16 }}>
              {span.months.map((m) => (
                <span key={m.ymd} style={{ position: 'absolute', left: `${pos(m.ymd)}%`, top: 6, ...wsType('meta'), color: WS_COLOR.textMuted, borderLeft: `1px solid ${WS_COLOR.border}`, paddingLeft: 4, whiteSpace: 'nowrap' }}>{m.label}</span>
              ))}
            </div>
          </div>
          <div data-pp-scroll style={{ overflow: 'auto', flex: 1, minHeight: 0 }} data-testid="review-plan" tabIndex={0} role="region" aria-label="Roadmap plan">
            {groupIds.map((g) => {
              const rows = plan.filter((r) => r.integration_group_id === g);
              const m = result.metrics.find((x) => x.integration_group_id === g);
              return (
                <div key={g}>
                  <div style={{ padding: '8px 16px', background: WS_COLOR.surfaceSubtle, display: 'flex', gap: 12, alignItems: 'baseline', position: 'sticky', top: 0, zIndex: 2 }}>
                    <span style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text }}>{g}</span>
                    <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{m?.group_name}</span>
                    <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginLeft: 'auto' }}>
                      Critical path: <strong style={{ color: WS_COLOR.text }}>{m?.critical_path.join(' → ') || '—'}</strong>
                    </span>
                  </div>
                  {rows.map((r) => {
                    const key = k(r.integration_group_id, r.application_id);
                    const meta = appMeta.get(key);
                    const orig = propByKey.get(key);
                    const changed = !samePlan(r, orig);
                    const iss = issuesByApp.get(key) || [];
                    const blockingHere = iss.filter((i) => i.severity === 'blocking').length;
                    const warnHere = iss.filter((i) => i.severity === 'warning').length;
                    const isOpen = expanded.has(key);
                    const deps = result.dependencies.filter((d) => (d.target_integration_group_id || d.integration_group_id) === g && d.target_application_id === r.application_id);
                    return (
                      <div key={key} data-testid="review-row" data-app={r.application_id} data-group={g}
                        style={{ borderBottom: `1px solid ${WS_COLOR.border}`, background: focusApp === key ? WS_STATUS.info.fill : undefined, transition: 'background 0.4s' }}>
                        <div onClick={() => toggle(key)} data-testid="review-row-toggle" style={{ display: 'grid', gridTemplateColumns: '260px 1fr', cursor: 'pointer', alignItems: 'center', minHeight: 56 }}>
                          <div style={{ padding: '8px 16px', minWidth: 0 }}>
                            <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                              <Icon icon={isOpen ? icons.ChevronDown : icons.ChevronRight} size={16} />
                              {r.application_id}
                              <DispositionChip value={meta?.recommendation || undefined} />
                              {changed && <span style={{ ...WS_CHIP, ...wsTone('info'), marginLeft: 4 }}>Adjusted</span>}
                            </div>
                            <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingLeft: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
                              {(() => { const st = statusOf(g, r.application_id); return st && LOCKED.has(st)
                                ? <span data-testid="review-locked" style={{ ...WS_CHIP, ...wsTone(st === 'Deployed' ? 'success' : 'info'), flexShrink: 0 }} title="Dates are locked; move the app back to Need Approval to change them"><Icon icon={icons.Lock} size={16} /> {st}</span>
                                : null; })()}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta?.application_name}</span>
                            </div>
                          </div>
                          <div style={{ position: 'relative', height: 40, marginRight: 16 }}>
                            {/* Owner request 2026-11-04: no phase bars beside the app ID, collapsed or expanded —
                                the phase editor (expanded) carries the dates. The issue counts stay. */}
                            <div style={{ position: 'absolute', right: -12, top: 10, display: 'flex', gap: 4 }}>
                              {blockingHere > 0 && <span style={{ ...WS_CHIP, ...wsTone('error') }} title="Blocking issues">{blockingHere}</span>}
                              {warnHere > 0 && <span style={{ ...WS_CHIP, ...wsTone('warning') }} title="Warnings">{warnHere}</span>}
                            </div>
                          </div>
                        </div>
                        {isOpen && (
                          <div style={{ padding: '4px 16px 14px 36px' }} data-testid="review-editor">
                            <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginBottom: 8 }}>
                              {deps.length > 0 && <>Depends on: {deps.map((d) => `${d.integration_group_id !== g ? `${d.integration_group_id} · ` : ''}${d.source_application_id} (${d.dependency_type === 'finish_to_start' ? 'after Go-Live and Support' : `from ${phaseLabel(d.source_phase_name)}`})`).join('; ')}. </>}
                              {autoFill && <>Change a start date and the end dates follow the effort plan ({PHASES.map((p) => `${p.short} ${effortOf(g, r.application_id, p.key)}d`).join(' · ')}).</>}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8 }}>
                              {PHASES.map((p) => {
                                const fill = GANTT_PHASE_FILL[p.name]!;
                                const was = orig?.phases[p.key];
                                const now = r.phases[p.key];
                                const moved = was && (was.start !== now.start || was.end !== now.end);
                                const bd = businessDays(now.start, now.end);
                                return (
                                  <div key={p.key} style={{ background: `${fill}55`, borderRadius: WS_RADIUS.field, padding: 8, borderTop: `4px solid ${fill}` }}>
                                    <div style={{ ...wsType('label'), color: WS_COLOR.text, display: 'flex', justifyContent: 'space-between' }}>
                                      {p.short}<span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{bd != null ? `${bd} bd` : ''}</span>
                                    </div>
                                    <input type="date" aria-label={`${r.application_id} ${p.name} start`} value={now.start || ''} disabled={LOCKED.has(statusOf(g, r.application_id) || '')} onChange={(e) => edit(g, r.application_id, p.key, 'start', e.target.value)} style={{ width: '100%', marginTop: 6 }} />
                                    <input type="date" aria-label={`${r.application_id} ${p.name} end`} value={now.end || ''} disabled={LOCKED.has(statusOf(g, r.application_id) || '')} onChange={(e) => edit(g, r.application_id, p.key, 'end', e.target.value)} style={{ width: '100%', marginTop: 4 }} />
                                    {moved && <div style={{ ...wsType('meta'), color: WS_STATUS.info.ink, marginTop: 4 }}>was {fmtMDY(was!.start)} – {fmtMDY(was!.end)}</div>}
                                  </div>
                                );
                              })}
                            </div>
                            {iss.length > 0 && (
                              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {iss.map((i) => (
                                  <div key={i.id} style={{ ...wsType('meta'), color: WS_STATUS[SEV[i.severity].tone === 'neutral' ? 'neutral' : SEV[i.severity].tone].ink }}>
                                    ● {i.message} <span style={{ color: WS_COLOR.textMuted }}>→ {i.recommendation}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {changed && <button className="btn" style={{ marginTop: 8 }} onClick={() => resetRow(g, r.application_id)}>Reset {r.application_id} to proposal</button>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Feasibility */}
        <div style={{ ...WS_CARD, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: 16, borderBottom: `1px solid ${WS_COLOR.border}`, flexShrink: 0 }} data-testid="review-verdict">
            <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              {ai && !aiStale ? 'AI feasibility review' : 'Rule check'}
            </div>
            {verdict && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                <span style={{ width: 40, height: 40, borderRadius: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...wsTone(VERDICT[verdict].tone) }}>
                  <Icon icon={VERDICT[verdict].icon} size={24} />
                </span>
                <div>
                  <div style={{ ...wsType('sectionTitle'), color: WS_STATUS[VERDICT[verdict].tone].ink }} data-testid="review-verdict-label">{VERDICT[verdict].label}</div>
                  <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
                    {result.counts.blocking} blocking · {result.counts.warning} warning{result.counts.warning === 1 ? '' : 's'} · {result.counts.info} note{result.counts.info === 1 ? '' : 's'}
                  </div>
                </div>
              </div>
            )}
            {ai && !aiStale && <div style={{ ...wsType('body'), color: WS_COLOR.text, marginTop: 10, fontWeight: 500 }}>{ai.headline}</div>}
            {aiStale && <div style={{ marginTop: 8 }}><Banner tone="info">The roadmap changed since the AI review. Run it again for an up-to-date verdict.</Banner></div>}
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className={ai && !aiStale ? 'btn' : 'btn btn-primary'} onClick={runAi} disabled={aiState === 'running'} data-testid="review-run-ai">
                <Icon icon={aiState === 'running' ? icons.Loader2 : icons.Sparkles} size={20} />
                {aiState === 'running' ? 'Reviewing…' : ai ? 'Run AI review again' : 'Run AI review'}
              </button>
              {aiState === 'running' && <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>The partner review reads every date, dependency and resource — about a minute.</span>}
            </div>
            {aiState === 'error' && <div style={{ marginTop: 8 }}><Banner tone="error">{aiError}</Banner></div>}
          </div>

          <div style={{ overflow: 'auto', flex: 1, minHeight: 0, padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }} tabIndex={0} role="region" aria-label="Feasibility review">
            <DependencyList result={result} plan={plan} focus={focus}
              onShow={(g, a) => { const key = k(g, a); reveal(key); setFocusApp(key); setTimeout(() => setFocusApp(null), 1600); document.querySelector(`[data-testid=review-row][data-app="${a}"][data-group="${g}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }}
              onClear={() => setFocus(null)} />
            {ai && !aiStale && (
              <section>
                <SectionLabel>Executive summary</SectionLabel>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {ai.executive_summary.map((s, i) => <li key={i} style={{ ...wsType('body'), color: WS_COLOR.text }}>{s}</li>)}
                </ul>
              </section>
            )}
            {ai && !aiStale && ai.critical_path_commentary && (
              <section>
                <SectionLabel>Critical path</SectionLabel>
                <div style={{ ...wsType('body'), color: WS_COLOR.text }}>{ai.critical_path_commentary}</div>
              </section>
            )}
            {ai && !aiStale && (ai.cross_group_explanations?.length || 0) > 0 && (
              <section data-testid="ai-cross-group">
                <SectionLabel>Cross-group dependencies</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ai.cross_group_explanations!.map((c, i) => (
                    <div key={i} data-testid="ai-cross-group-item" data-governs={c.governs} data-respected={c.respected === null ? 'unknown' : String(c.respected)}
                      style={{ border: `1px solid ${WS_COLOR.border}`, borderLeft: `4px solid ${c.respected === false ? WS_STATUS.error.ink : c.respected ? WS_STATUS.success.ink : WS_COLOR.border}`, borderRadius: WS_RADIUS.field, padding: '8px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text }}>{c.target_group} · {c.target_application_id}</span>
                        <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>depends on</span>
                        <span style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text }}>{c.source_group} · {c.source_application_id}</span>
                        <span style={{ ...WS_CHIP, ...wsTone(c.governs === 'no_source_schedule' ? 'warning' : 'info'), marginLeft: 'auto' }}>{GOVERNS[c.governs]}</span>
                      </div>
                      <div style={{ ...wsType('meta'), color: WS_COLOR.text, marginTop: 4 }}>
                        Starts {c.rule}: <strong>{fmtMDY(c.dependency_date)}</strong> · {c.target_group} earliest start <strong>{fmtMDY(c.group_earliest_start)}</strong> · no earlier than <strong>{fmtMDY(c.earliest_start)}</strong> · planned <strong>{fmtMDY(c.proposed_start)}</strong>
                      </div>
                      {c.explanation && <div style={{ ...wsType('body'), color: WS_COLOR.text, marginTop: 6 }}>{c.explanation}</div>}
                    </div>
                  ))}
                </div>
              </section>
            )}
            <section data-testid="review-issues">
              <SectionLabel>Issues {result.issues.length + (ai && !aiStale ? ai.issues.length : 0) > 0 ? '' : '— none'}</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {result.issues.filter((i) => i.severity !== 'info').map((i) => (
                  <IssueCard key={i.id} tone={SEV[i.severity].tone} label={SEV[i.severity].label} title={i.rule}
                    apps={i.application_id ? [i.application_id] : []} group={i.integration_group_id} body={i.message} action={i.recommendation} source="Rule"
                    onGo={i.application_id ? () => { const key = k(i.integration_group_id, i.application_id!); reveal(key); setFocusApp(key); setTimeout(() => setFocusApp(null), 1600); document.querySelector(`[data-testid=review-row][data-app="${i.application_id}"][data-group="${i.integration_group_id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }); } : undefined} />
                ))}
                {ai && !aiStale && ai.issues.map((i, n) => (
                  <IssueCard key={`ai${n}`} tone={SEV[i.severity].tone} label={SEV[i.severity].label} title={i.category[0]!.toUpperCase() + i.category.slice(1)}
                    apps={i.application_ids} group={i.integration_group_id} body={`${i.finding} ${i.impact}`} action={i.recommendation} source="AI" />
                ))}
                {result.issues.filter((i) => i.severity === 'info').length > 0 && (
                  <details>
                    <summary style={{ ...wsType('meta'), color: WS_COLOR.textMuted, cursor: 'pointer' }}>{result.issues.filter((i) => i.severity === 'info').length} calendar note(s)</summary>
                    {result.issues.filter((i) => i.severity === 'info').map((i) => (
                      <div key={i.id} style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 4 }}>{i.application_id}: {i.message}</div>
                    ))}
                  </details>
                )}
              </div>
            </section>
            {ai && !aiStale && ai.recommendations.length > 0 && (
              <section>
                <SectionLabel>Recommendations</SectionLabel>
                <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {ai.recommendations.map((s, i) => <li key={i} style={{ ...wsType('body'), color: WS_COLOR.text }}>{s}</li>)}
                </ol>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Every dependency of the roadmap, checked against the CURRENT plan: what each app waits for,
 * the earliest date that allows, the planned start, and whether it holds. Filtered to one app
 * when an app is open in the roadmap (owner request 2026-11-04).
 */
function DependencyList({ result, plan, focus, onShow, onClear }: {
  result: ReviewResult; plan: PlanRow[]; focus: string | null;
  onShow: (g: string, a: string) => void; onClear: () => void;
}) {
  const find = (g: string, a: string) => plan.find((r) => r.integration_group_id === g && r.application_id === a);
  const [fg, fa] = focus ? focus.split('|') : [null, null];
  const tg = (d: ReviewResult['dependencies'][number]) => d.target_integration_group_id || d.integration_group_id;
  const deps = result.dependencies.filter((d) => !focus || (tg(d) === fg && d.target_application_id === fa) || (d.integration_group_id === fg && d.source_application_id === fa));
  const rows = deps.map((d) => {
    const cross = tg(d) !== d.integration_group_id;
    const srcRow = find(d.integration_group_id, d.source_application_id);
    const src = srcRow || (d.source_phases ? { integration_group_id: d.integration_group_id, application_id: d.source_application_id, phases: d.source_phases } as PlanRow : undefined);
    const tgt = find(tg(d), d.target_application_id);
    const srcName = cross ? `${d.integration_group_id} · ${d.source_application_id}` : d.source_application_id;
    let earliest: string | null = null;
    let rule = '';
    if (d.dependency_type === 'finish_to_start') {
      const end = src?.phases.golive.end;
      earliest = end ? nextBiz(end, 1 + (Number(d.lag_days) || 0)) : null;
      rule = `after ${srcName} finishes Go-Live and Support${Number(d.lag_days) ? ` (+${d.lag_days} days)` : ''}`;
    } else {
      const ph = PHASES.find((p) => p.name === d.source_phase_name);
      const s = ph ? src?.phases[ph.key].start : null;
      earliest = s ? onOrAfterBiz(s) : null;
      rule = `when ${srcName} reaches ${phaseLabel(d.source_phase_name)}`;
    }
    const planned = tgt?.phases.assessment.start || null;
    const ok = !earliest || !planned ? null : planned >= earliest;
    return { d, earliest, planned, ok, rule };
  });
  const met = rows.filter((r) => r.ok === true).length;
  const broken = rows.filter((r) => r.ok === false).length;
  return (
    <section data-testid="review-dependencies">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <SectionLabel>Dependencies {focus ? `— ${fa}` : ''}</SectionLabel>
        <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginBottom: 8 }}>
          {rows.length ? `${met} met${broken ? ` · ${broken} broken` : ''}` : ''}
        </span>
        {focus && <button className="btn" style={{ marginLeft: 'auto', marginBottom: 8 }} onClick={onClear}>Show all</button>}
      </div>
      {rows.length === 0 ? (
        <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{focus ? `${fa} has no dependencies in ${fg}.` : 'No dependencies are defined for these groups.'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(({ d, earliest, planned, ok, rule }, i) => (
            <div key={i} data-testid="review-dependency" data-ok={ok === null ? 'unknown' : String(ok)}
              style={{ border: `1px solid ${WS_COLOR.border}`, borderLeft: `4px solid ${ok === false ? WS_STATUS.error.ink : ok ? WS_STATUS.success.ink : WS_COLOR.border}`, borderRadius: WS_RADIUS.field, padding: '6px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ color: ok === false ? WS_STATUS.error.ink : ok ? WS_STATUS.success.ink : WS_COLOR.textMuted }}>
                  <Icon icon={ok === false ? icons.XCircle : ok ? icons.CheckCircle2 : icons.Info} size={16} />
                </span>
                <button className="btn" style={{ padding: '0 8px', minHeight: 0, height: 24, ...wsType('mono'), fontWeight: 600 }} onClick={() => onShow(tg(d), d.target_application_id)}>{tg(d) !== d.integration_group_id ? `${tg(d)} · ` : ''}{d.target_application_id}</button>
                <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>depends on</span>
                <button className="btn" style={{ padding: '0 8px', minHeight: 0, height: 24, ...wsType('mono'), fontWeight: 600 }} onClick={() => onShow(d.integration_group_id, d.source_application_id)}>{tg(d) !== d.integration_group_id ? `${d.integration_group_id} · ` : ''}{d.source_application_id}</button>
                <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginLeft: 'auto' }}>{tg(d) !== d.integration_group_id ? `${d.integration_group_id} → ${tg(d)}` : d.integration_group_id} · {d.dependency_type === 'finish_to_start' ? 'Finish → Start' : 'Phase-based'}</span>
              </div>
              <div style={{ ...wsType('meta'), color: WS_COLOR.text, marginTop: 4 }}>
                Starts {rule}: no earlier than <strong>{fmtMDY(earliest)}</strong> · planned <strong>{fmtMDY(planned)}</strong>
                {ok === false && <span style={{ color: WS_STATUS.error.ink }}> — {Math.round((toDate(earliest!).getTime() - toDate(planned!).getTime()) / 86400000)} days too early</span>}
              </div>
              {d.notes && <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 2 }}>{d.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: 'success' | 'warning' | 'info' | 'neutral' | 'error' }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{label}</div>
      <div style={{ ...wsType('sectionTitle'), color: WS_COLOR.text, whiteSpace: 'nowrap' }}>{value}</div>
      <div style={{ ...wsType('meta'), color: tone === 'neutral' ? WS_COLOR.textMuted : WS_STATUS[tone].ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ ...wsType('meta'), fontWeight: 600, color: WS_COLOR.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>{children}</div>;
}

function IssueCard({ tone, label, title, apps, group, body, action, source, onGo }: {
  tone: 'error' | 'warning' | 'info' | 'neutral'; label: string; title: string; apps: string[]; group: string; body: string; action: string; source: 'Rule' | 'AI'; onGo?: () => void;
}) {
  return (
    <div data-testid="review-issue" data-source={source} style={{ border: `1px solid ${WS_COLOR.border}`, borderLeft: `4px solid ${tone === 'neutral' ? WS_COLOR.border : WS_STATUS[tone].ink}`, borderRadius: WS_RADIUS.field, padding: '8px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ ...WS_CHIP, ...wsTone(tone) }}>{label}</span>
        <span style={{ ...wsType('label'), color: WS_COLOR.text }}>{title}</span>
        <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{group}{apps.length ? ` · ${apps.join(', ')}` : ''}</span>
        <span style={{ ...WS_CHIP, ...wsTone(source === 'AI' ? 'info' : 'neutral'), marginLeft: 'auto' }}>{source === 'AI' ? <><Icon icon={icons.Sparkles} size={16} /> AI</> : 'Rule'}</span>
      </div>
      <div style={{ ...wsType('body'), color: WS_COLOR.text, marginTop: 4 }}>{body}</div>
      <div style={{ ...wsType('meta'), color: WS_COLOR.text, marginTop: 4, display: 'flex', gap: 6 }}>
        <Icon icon={icons.ArrowRight} size={16} /><span>{action}</span>
      </div>
      {onGo && <button className="btn" style={{ marginTop: 6 }} onClick={onGo}>Show in roadmap</button>}
    </div>
  );
}

function monthTicks(a?: string, b?: string): { ymd: string; label: string }[] {
  if (!a || !b) return [];
  const out: { ymd: string; label: string }[] = [];
  const s = new Date(a + 'T12:00:00');
  const e = new Date(b + 'T12:00:00');
  const d = new Date(s.getFullYear(), s.getMonth() + 1, 1, 12);
  const step = Math.max(1, Math.ceil(((e.getFullYear() - s.getFullYear()) * 12 + e.getMonth() - s.getMonth()) / 12));
  for (let i = 0; d <= e; i++, d.setMonth(d.getMonth() + 1)) {
    if (i % step) continue;
    out.push({ ymd: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`, label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) });
  }
  return out;
}
